import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Thunk: Fetch financial details
export const fetchFinancialDetails = createAsyncThunk(
  "users/fetchFinancialDetails",
  async (profileId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${apiUrl}/users/${profileId}/financial-details/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch financial details"
      );
    }
  }
);

// Thunk: Fetch transaction history with pagination
export const fetchTransactionHistory = createAsyncThunk(
  "users/fetchTransactionHistory",
  async ({ profileId, page = 1, pageSize = 20, type, status, dateFrom, dateTo }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page,
        page_size: pageSize,
        ...(type && { type }),
        ...(status && { status }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      });

      const response = await axios.get(
        `${apiUrl}/users/${profileId}/transaction-history/?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return { ...response.data, page };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch transaction history"
      );
    }
  }
);

// Thunk: Fetch wallet logs with pagination
export const fetchWalletLogs = createAsyncThunk(
  "users/fetchWalletLogs",
  async ({ profileId, page = 1, pageSize = 20 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${apiUrl}/users/${profileId}/wallet-logs/?page=${page}&page_size=${pageSize}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      return { ...response.data, page };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch wallet logs"
      );
    }
  }
);

const initialState = {
  financialDetails: null,
  transactionHistory: {
    results: [],
    count: 0,
    currentPage: 1,
    totalPages: 1,
    summary: null,
    hasMore: false,
  },
  walletLogs: {
    results: [],
    count: 0,
    currentPage: 1,
    totalPages: 1,
    summary: null,
    hasMore: false,
  },
  loading: {
    financial: false,
    transactions: false,
    walletLogs: false,
  },
  error: {
    financial: null,
    transactions: null,
    walletLogs: null,
  },
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    resetTransactionHistory: (state) => {
      state.transactionHistory = initialState.transactionHistory;
    },
    resetWalletLogs: (state) => {
      state.walletLogs = initialState.walletLogs;
    },
    resetFinancialDetails: (state) => {
      state.financialDetails = null;
      state.error.financial = null;
    },
  },
  extraReducers: (builder) => {
    // Financial Details
    builder
      .addCase(fetchFinancialDetails.pending, (state) => {
        state.loading.financial = true;
        state.error.financial = null;
      })
      .addCase(fetchFinancialDetails.fulfilled, (state, action) => {
        state.loading.financial = false;
        state.financialDetails = action.payload;
      })
      .addCase(fetchFinancialDetails.rejected, (state, action) => {
        state.loading.financial = false;
        state.error.financial = action.payload;
      });

    // Transaction History
    builder
      .addCase(fetchTransactionHistory.pending, (state) => {
        state.loading.transactions = true;
        state.error.transactions = null;
      })
      .addCase(fetchTransactionHistory.fulfilled, (state, action) => {
        state.loading.transactions = false;
        const { results, count, current_page, total_pages, summary, page } = action.payload;
        
        // If it's page 1, replace; otherwise, append
        if (page === 1) {
          state.transactionHistory.results = results;
        } else {
          state.transactionHistory.results = [
            ...state.transactionHistory.results,
            ...results,
          ];
        }
        
        state.transactionHistory.count = count;
        state.transactionHistory.currentPage = current_page;
        state.transactionHistory.totalPages = total_pages;
        state.transactionHistory.summary = summary;
        state.transactionHistory.hasMore = current_page < total_pages;
      })
      .addCase(fetchTransactionHistory.rejected, (state, action) => {
        state.loading.transactions = false;
        state.error.transactions = action.payload;
      });

    // Wallet Logs
    builder
      .addCase(fetchWalletLogs.pending, (state) => {
        state.loading.walletLogs = true;
        state.error.walletLogs = null;
      })
      .addCase(fetchWalletLogs.fulfilled, (state, action) => {
        state.loading.walletLogs = false;
        const { results, count, current_page, total_pages, summary, page } = action.payload;
        
        // If it's page 1, replace; otherwise, append
        if (page === 1) {
          state.walletLogs.results = results;
        } else {
          state.walletLogs.results = [
            ...state.walletLogs.results,
            ...results,
          ];
        }
        
        state.walletLogs.count = count;
        state.walletLogs.currentPage = current_page;
        state.walletLogs.totalPages = total_pages;
        state.walletLogs.summary = summary;
        state.walletLogs.hasMore = current_page < total_pages;
      })
      .addCase(fetchWalletLogs.rejected, (state, action) => {
        state.loading.walletLogs = false;
        state.error.walletLogs = action.payload;
      });
  },
});


export const { resetTransactionHistory, resetWalletLogs, resetFinancialDetails } = userSlice.actions;
export default userSlice.reducer;